// [2026-09-06] ADDED — the analyses this company has submitted to the video-processing API.
//
// WHAT A ROW IS, AND THE ONE THING IT IS NOT
// -------------------------------------------
// Every row is a SUBMISSION RECORD: the analysis engine accepted this job, on this date, for this
// room, and it cost this much. It is NOT a report, and it is not a completion. Three upstream
// behaviours make that distinction load-bearing rather than pedantic (all three documented in
// app/core/video_processing_proxy.py's header):
//
//   1. NO FAILURE CALLBACK. A job whose download or frame extraction fails ends SILENTLY. From
//      outside, an accepted job and a dead job are indistinguishable.
//   2. THE ENGINE'S RESULT STORE IS OFF. GET /results/{job_id} answers "pending" for every id —
//      finished, failed and never-submitted alike.
//   3. REPORTS GO TO A WEBHOOK, never to the caller of the API.
//
// So this screen deliberately does NOT render a status like "completed" or a progress spinner. It
// would have to invent one, and the invented value would read as fact. What it shows instead is
// what this service actually knows — that the job was accepted and charged — and it says plainly
// where the finished report goes. A dashboard that says "processing…" forever beside a job that
// finished days ago is worse than one that explains the situation once.
//
// WHY THE DATA COMES FROM US AND NOT THE ENGINE
// ----------------------------------------------
// The engine exposes no way to enumerate a client's jobs and does not record which client owns one.
// These rows exist only because this service writes them at submission time
// (app/core/video_job_store.py) — which is also why this screen works while the upstream is down,
// and why it costs nothing to open.
//
// REMAINING LIMIT, STATED RATHER THAN HIDDEN: ownership rows only exist for jobs submitted after
// that store was introduced. Older jobs are not listed here and cannot be — nothing recorded them.

import { useEffect, useState } from 'react'
import { platform, platformProofObjectUrl } from '../../api.js'
import { credits as fmtCredits, clock, num, when } from '../../ui.jsx'
import DataTable from './DataTable.jsx'
import { PfChip, PfEmpty, PfNotice, PfSkeleton, PfModal } from '../../components/platform/ui.jsx'
// [2026-09-16] ADDED — this report now draws the same Proctoring Logs layout as the SDK session
// report (PlatformReports.jsx), asked for by Rujal on 2026-09-16: "same UI and format, but with
// correct timings". Both screens are read by the same person about the same candidate, so they
// should not be two different screens.
import {
  CameraIcon, ClockIcon, EyeIcon, MonitorIcon,
} from '../../components/platform/icons.jsx'
import logoUrl from '../../assets/incruiter-logo.svg'

export default function PlatformVideoJobs({ onError }) {
  const [openId, setOpenId] = useState(null)
  // Separate from `openId`: the submission record and the findings are two different modals, and a
  // reviewer opening one must not implicitly open the other.
  const [reportId, setReportId] = useState(null)

  // SERVER-SIDE paging, search and date filtering — the same mapping PlatformReports uses for the
  // sessions tab, so the two tabs of this screen behave identically. `total` comes back counted with
  // the SAME filters as the page, which is what lets the "showing 1-25 of N" line be trusted.
  async function fetchPage({ limit, offset, search, dateFrom, dateTo, order }) {
    try {
      const d = await platform.videoJobs({ limit, offset, search,
                                           date_from: dateFrom, date_to: dateTo, order })
      return { rows: d.jobs || [], total: d.total || 0 }
    } catch (err) { onError?.(err); throw err }
  }

  const columns = [
    { key: 'job_id', label: 'Job ID',
      render: (r) => <span className="mono">{r.job_id}</span> },
    { key: 'room_id', label: 'Room',
      render: (r) => r.room_id
        ? <span className="mono">{r.room_id}</span>
        : <span className="muted">—</span> },
    { key: 'interview_id', label: 'Interview',
      render: (r) => r.interview_id
        ? <span className="mono">{r.interview_id}</span>
        : <span className="muted">—</span> },
    { key: 'request_source', label: 'Source',
      render: (r) => r.request_source || <span className="muted">—</span> },
    // [2026-09-11] CHANGED — the Credits column is off. Asked for by Rujal, and the data agrees:
    // it can only ever read 0.00.
    //
    // OLD:
    //   // `credits_charged` is derived SERVER-SIDE from credits_charged_milli
    //   // (app/modules/me/route.py) rather than divided by 1000 here — that is how a billing
    //   // column starts disagreeing with the ledger the first time the price stops being a round
    //   // number.
    //   { key: 'credits_charged', label: 'Credits', align: 'right',
    //     render: (r) => num(r.credits_charged, { decimals: 2 }) },
    //
    // WHY IT IS STRUCTURALLY ZERO. Interview billing HOLDS a credit when the candidate session is
    // minted and SETTLES it when the analysis completes — the money never moves through the analyse
    // request, so nothing writes `credits_charged_milli` on the job row. Verified against the live
    // account on 2026-09-11: job 20f60820-d168-4da0-b1eb-56d709c0118e reads
    // `credits_charged_milli: 0` while `settle_outcome: settled_analysis_complete`. Both are
    // correct; they are answers to different questions.
    //
    // A column of 0.00 beside genuinely charged interviews does not merely fail to inform — it
    // asserts that these analyses were free. The ledger on Credits & Billing is where the real
    // movements are, one row per reserve / release / settle.
    //
    // This is the same removal PR #20 made on API Requests and Proctoring Requests, for the same
    // reason; this table was simply missed then. The key stays defined and commented rather than
    // deleted, so restoring it is uncommenting two lines — and so the reason survives with it.
    // [2026-09-25] CHANGED — `orderSort: true`: the up/down date arrow, ordering the WHOLE result
    // set newest/oldest first through the server's `order` (see DataTable). This is the one
    // column the server sorts by (created_at).
    // OLD: { key: 'created_at', label: 'Submitted',
    { key: 'created_at', label: 'Submitted', orderSort: true,
      render: (r) => <span className="muted">{when(r.created_at)}</span> },
    // [2026-09-06] ADDED — the findings, now that the engine's result store returns them.
    //
    // A SEPARATE CONTROL FROM THE ROW, which opens the submission record. The two are different
    // things and this is exactly the case the DataTable note warns about: the row action and the
    // button action DIFFER here, so stopPropagation is load-bearing rather than tidy — without it
    // a click would open both modals, one behind the other.
    {
      key: 'report', label: 'Report', align: 'right',
      render: (r) => (
        <button className="pf-btn ghost sm"
                title="Open the proctoring findings for this analysis"
                aria-label={`Open the report for job ${r.job_id}`}
                onClick={(event) => { event.stopPropagation(); setReportId(r.job_id) }}>
          View
        </button>
      ),
    },
  ]

  return (
    <>
      <DataTable
        columns={columns}
        fetchPage={fetchPage}
        pageSize={25}
        searchPlaceholder="Search job id, room, interview or source…"
        filterLabel="Analyses"
        // No `filters` array: sessions filter by state, and a submission record HAS no state — see
        // the module header on why a status column here would have to be invented. Date range and
        // ordering are offered, which is the rest of what the sessions tab gives.
        onRowClick={(r) => setOpenId(r.job_id)}
        rowKey={(r) => r.job_id}
        emptyTitle="No analyses submitted yet"
        emptyHint="Recordings sent for proctoring analysis appear here, newest first."
        csvName="analysis-jobs"
      />

      {openId && (
        <JobModal jobId={openId} onClose={() => setOpenId(null)} onError={onError} />
      )}

      {reportId && (
        <ReportModal jobId={reportId} onClose={() => setReportId(null)} onError={onError} />
      )}
    </>
  )
}

/** One submitted analysis, in full.
 *
 *  Re-fetched by id rather than handed the row the table already holds. The row in the table came
 *  from a list that may be minutes old by the time someone clicks it, and this is also the call
 *  that enforces the tenant scope server-side — `video_job_store.get` puts client_id in the QUERY,
 *  so another company's job is not-found rather than found-and-then-refused.
 */
function JobModal({ jobId, onClose, onError }) {
  const [state, setState] = useState({ job: null, error: null, loading: true })

  useEffect(() => {
    let live = true
    platform.videoJob(jobId)
      .then((job) => { if (live) setState({ job, error: null, loading: false }) })
      .catch((err) => {
        if (!live) return
        onError?.(err)
        setState({ job: null, error: err, loading: false })
      })
    return () => { live = false }
  }, [jobId, onError])

  const { job, error, loading } = state

  return (
    <PfModal onClose={onClose}
             title="Analysis job"
             subtitle={job?.created_at ? `Submitted ${when(job.created_at)}` : undefined}>
      {loading && <PfSkeleton height={240} />}
      {error && <PfNotice kind="error">{error.message}</PfNotice>}
      {job && (
        <div style={{ display: 'grid', gap: 14 }}>
          {/* WHAT THIS RECORD IS. Stated first and in full, because everything below is only
              meaningful once the reader knows a row is an acceptance and not a result. Without this
              the modal reads as a report that is mysteriously missing its findings. */}
          <PfNotice kind="info">
            <strong>This is a submission record, not a report.</strong> It confirms the analysis
            engine accepted this job and what it cost. The finished proctoring summary is delivered
            to your configured webhook — it does not appear here, and the engine sends no callback
            if an analysis fails after being accepted.
          </PfNotice>

          <Field label="Job ID" value={job.job_id} mono
                 note="The engine's id for this analysis. Quote it in any support request." />
          <Field label="Task ID" value={job.task_id}
                 mono note="The engine's internal worker handle — tracing only." />
          <Field label="Room" value={job.room_id} mono />
          <Field label="Interview" value={job.interview_id} mono />
          <Field label="Request source" value={job.request_source}
                 note="Which InCruiter product submitted it — this chooses the webhook the report is sent to." />
          {/* [2026-09-17] CHANGED — the same structural zero the Credits COLUMN was retired for
              on 2026-09-11 (see the note in `columns` above). The column went; this FIELD was
              left, and it kept asserting that a genuinely paid-for analysis was free.

              `credits_charged_milli` is never written on a job row under interview billing — the
              credit is held at the mint and settled by the settler, so the money never passes
              through the analyse request. Verified on the live account 2026-09-11: job
              20f60820-… reads credits_charged_milli 0 while settle_outcome is
              settled_analysis_complete. Both correct; they answer different questions.

              OLD: value={num(job.credits_charged, { decimals: 2 })} */}
          <Field label="Credits charged"
                 value={job.credits_charged
                   ? fmtCredits(job.credits_charged)
                   : 'Billed with the interview, not this job'}
                 note="A resumed interview costs nothing extra — the ledger deduplicates it." />
          <Field label="Submitted" value={when(job.created_at)} />
          <Field label="Request ID" value={job.request_id} mono
                 note="This service's id for the submitting call. Names the ledger entry for the charge." />
        </div>
      )}
    </PfModal>
  )
}

/** One labelled value. Renders an em dash for anything absent rather than an empty row, so a field
 *  the customer did not supply is visibly blank instead of looking like a rendering fault. */
function Field({ label, value, note, mono }) {
  return (
    <div>
      <div className="pf-tile-label">{label}</div>
      <div className={mono ? 'mono' : undefined} style={{ fontSize: 14, marginTop: 2 }}>
        {value == null || value === '' ? <span className="muted">—</span> : value}
      </div>
      {note && <div className="pf-tile-sub" style={{ fontSize: 11.5, marginTop: 2 }}>{note}</div>}
    </div>
  )
}

// [2026-09-06] ADDED — the finished proctoring findings, written for a NON-TECHNICAL reader.
//
// WHO THIS SCREEN IS FOR. A recruiter or hiring manager deciding whether to trust an interview.
// They do not know what a yaw ratio is and should never be shown one. The engine's raw report is
// 2,686 frames of model output (gaze vectors, pitch ratios, per-frame booleans) plus 91 summarised
// intervals; ONLY the intervals are human-readable, so only they are rendered, in plain language
// with a picture beside each one.
//
// THE TRANSLATION IS THE WHOLE POINT:
//   looking_away_intervals  ->  "Looked away from the screen"
//   face_issue_intervals    ->  "Face not clearly visible"      (+ absent / partial / multiple)
//   device_intervals        ->  "Phone or second screen visible"
//   drowsiness_percent      ->  "Signs of drowsiness"
//
// WHY THE IMAGES LOAD ONE AT A TIME. Every interval carries a full base64 JPEG in the engine's
// payload — 30-58 KB each, and 4.3 MB for the report measured on this deployment. The summary
// without them is 12 KB. So the list renders instantly from the summary and each frame is fetched
// when its row is expanded (app/modules/me/route.py's proof route). A reviewer looks at a handful
// of the 91, not all of them.
//
// WHAT THIS SCREEN DELIBERATELY DOES NOT DO: score the candidate, or say "cheated". It reports what
// was observed, with the evidence and the timestamp, and leaves the judgement to the person. An
// automated verdict on a hiring decision is not something a proctoring tool should be asserting —
// and the model's own flags are noisy enough that presenting one as a conclusion would be wrong.

// The three interval kinds, in the order a reviewer should read them, with the plain-English
// labels that replace the engine's field names on screen.
// `short` is the summary-tile caption, `label` the section heading — a tile 150px wide cannot carry
// "Phone or second screen visible" without wrapping to three lines.
const FINDINGS = [
  { key: 'looking_away_intervals', short: 'Looked away',
    label: 'Looked away from the screen',
    hint: 'The candidate’s gaze left the screen for a sustained period.' },
  { key: 'face_issue_intervals', short: 'Face not visible',
    label: 'Face not clearly visible',
    hint: 'The camera could not see one clear face — absent, partially out of frame, or more than one person.' },
  { key: 'device_intervals', short: 'Phone / 2nd screen',
    label: 'Phone or second screen visible',
    hint: 'An object associated with cheating appeared in view.' },
]

/** How concerning is this many seconds of a given behaviour? Drives the chip beside each heading.
 *
 *  THESE THRESHOLDS ARE PRESENTATIONAL, NOT A VERDICT. They exist so a reviewer's eye lands on the
 *  60-second phone sighting rather than on a 3-second glance away, and they are deliberately
 *  generous: looking away briefly is what people do when they think. Nothing here is stored, and
 *  no decision is made from it.
 */
function severityOf(kind, totalSec, count) {
  // 'live' is the GREEN chip. There is no `good` tone in platform-theme.css — the
  // defined set is live / warn / bad / idle, and an undefined class renders unstyled.
  if (!count) return { tone: 'live', word: 'None' }
  if (kind === 'device_intervals') return { tone: 'bad', word: 'Review' }
  if (totalSec >= 300) return { tone: 'bad', word: 'Review' }
  if (totalSec >= 60) return { tone: 'warn', word: 'Some' }
  return { tone: 'idle', word: 'Minor' }
}

/** "10:15:42" from an ISO timestamp — the clock time a reviewer would scrub to in the recording. */
function timeOf(iso) {
  if (typeof iso !== 'string') return '—'
  const match = iso.match(/T(\d{2}:\d{2}:\d{2})/)
  return match ? match[1] : iso
}

// ── [2026-09-16] ADDED — correct timings for this report ──────────────────────
//
// WHAT WAS WRONG. `timeOf` above prints the engine's timestamp as a clock time, and its docstring
// calls it "the clock time a reviewer would scrub to". It is not a clock time. Measured on deployed
// test, job d2d331f0: every frame runs from exactly 2026-09-13T11:00:00 to exactly 11:30:00 — 3601
// frames, a perfectly round thirty minutes — while the recording's own filename carries the epoch
// 11:04:18. The engine stamps from a SYNTHETIC ORIGIN. So "11:00:00" on this screen told a reviewer
// the candidate looked away at eleven o'clock, and that is simply not a fact we hold.
//
// WHAT IS RIGHT. The OFFSET into the recording — "4:32" — which is both true and the number a
// reviewer actually needs, because it is what they type into the video player. The origin comes
// from the report's own `recording_start` (app/core/video_processing_proxy.py:summarise_report),
// the first frame's timestamp, rather than from the first FLAGGED moment — a recording whose first
// finding is nine minutes in must not report that finding as 0:00.
//
// FALLS BACK RATHER THAN FAILING: with no `recording_start` (an engine payload that carried no
// frames) the raw timestamp is shown exactly as it was before this change.

function offsetOf(iso, origin) {
  if (typeof iso !== 'string') return null
  const at = new Date(iso).getTime()
  const zero = origin ? new Date(origin).getTime() : NaN
  if (!Number.isFinite(at) || !Number.isFinite(zero)) return null
  return Math.max(Math.round((at - zero) / 1000), 0)
}

/** "4:32 into the recording", or the raw stamp when there is no origin to measure from. */
function atLabel(iso, origin) {
  const off = offsetOf(iso, origin)
  return off == null ? timeOf(iso) : clock(off)
}

/** Every flagged moment from all three kinds, merged and ordered as they happened.
 *
 *  ONE TIMELINE RATHER THAN THREE LISTS, which is the point of the layout: a reviewer wants to know
 *  what happened in the sitting, in order — not to read thirty look-aways, then six face problems,
 *  and reconstruct the sequence themselves. The `kind` travels with each row, so nothing is
 *  flattened away: the heading each moment came from is still on its sentence.
 */
function momentsOf(report) {
  const out = []
  for (const finding of FINDINGS) {
    for (const row of report?.[finding.key] || []) {
      out.push({ finding, row, at: new Date(row.start).getTime() })
    }
  }
  return out.sort((a, b) => (a.at || 0) - (b.at || 0))
}

/** The dot colour for ONE moment. Reuses severityOf so the timeline and the headings cannot
 *  disagree about what counts as serious — same thresholds, applied to a single interval.
 *
 *  `idle` MAPS TO GREY, NOT GREEN, and the distinction matters. Green on the SDK timeline means an
 *  ordinary, expected event — a camera starting, a tab coming back. Every row on THIS timeline is a
 *  finding: the model flagged it. A green dot beside "Looked away from the screen for 26 seconds"
 *  tells a reviewer it was fine, which is not a judgement this screen is entitled to make — the
 *  module header is explicit that it reports observations and leaves the conclusion to the person.
 *  Grey says "minor", which is what severityOf's lowest band actually means.
 */
const DOT_BY_TONE = { bad: 'high', warn: 'medium', idle: 'idle', live: 'idle' }

function ReportModal({ jobId, onClose, onError }) {
  const [state, setState] = useState({ report: null, error: null, loading: true })
  // A frame opened full-size. Escape closes THIS first and only then the modal — otherwise
  // examining a picture and pressing Escape would shut the whole report and lose the reviewer's
  // place. Same rule the session report already follows.
  const [lightbox, setLightbox] = useState(null)

  useEffect(() => {
    let live = true
    platform.videoReport(jobId)
      .then((report) => { if (live) setState({ report, error: null, loading: false }) })
      .catch((err) => {
        if (!live) return
        // NOT routed to onError: a 404 here means "this analysis has no finished report yet", an
        // ordinary state rather than an application error worth a red banner on the page behind.
        setState({ report: null, error: err, loading: false })
      })
    return () => { live = false }
  }, [jobId])

  useEffect(() => {
    if (!lightbox) return undefined
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); setLightbox(null) } }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [lightbox])

  const { report, error, loading } = state
  const totals = report?.totals || {}
  const drowsy = report?.drowsiness_percent

  // [2026-09-16] ADDED — the recording's own origin and length, and the merged timeline.
  //
  // `origin` is the first frame's timestamp (recording_start), which is what turns the engine's
  // synthetic clock into a true offset. `recorded` is the length in seconds, shown in the header in
  // place of a start time — see the note on offsetOf for why a clock time there would be a fiction.
  const origin = report?.recording_start || null
  const recorded = origin && report?.recording_end
    ? Math.max(Math.round((new Date(report.recording_end) - new Date(origin)) / 1000), 0)
    : null
  const moments = report ? momentsOf(report) : []
  // Only the moments that actually have a picture become cards. `has_proof` is the server's own
  // flag, so a card is never rendered over an image that was never sent.
  const withProof = moments.filter(({ row }) => row.has_proof)

  return (
    <PfModal wide onClose={onClose}
             title="Proctoring report"
             subtitle={report?.room_id ? `Room ${report.room_id}` : undefined}>
      {loading && <PfSkeleton height={320} />}

      {error && (
        <PfNotice kind="info">
          <strong>No report yet.</strong> The analysis engine has not returned findings for this
          job. Reports are delivered by webhook when an analysis completes, and the engine sends
          nothing at all if one fails — so a job submitted a long time ago with no report here
          most likely did not complete.
        </PfNotice>
      )}

      {report && (
        <div className="pf-report">
          {/* [2026-09-16] CHANGED — this body is rebuilt to the Proctoring Logs layout the SDK
              session report uses, and every time on it is now an OFFSET INTO THE RECORDING rather
              than the engine's synthetic clock. See the note on offsetOf.

              WHAT MOVED:
                · the four-tile strip. Its numbers are all still here — each kind's count and total
                  are on the timeline heading, and drowsiness is in the Headline sentence, which now
                  states it whenever the engine reported one.
                · the three per-kind sections, each with its own picture grid, become ONE timeline
                  and ONE picture grid. Nothing is filtered: every interval from all three kinds is
                  a row, and every interval that has a frame is a card.

              The old render is kept below, whole and commented, exactly as in PlatformReports.jsx.

              --- OLD (pre-2026-09-16) ---
              <div style={{ display: 'grid', gap: 20 }}>
                <Headline totals={totals} drowsy={drowsy} />

                <div style={{ display: 'grid', gap: 10,
                              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
                  {FINDINGS.map((finding) => {
                    const total = totals[finding.key] || { count: 0, duration_sec: 0 }
                    const sev = severityOf(finding.key, total.duration_sec, total.count)
                    return (
                      <div key={finding.key} className="pf-tile" style={{ padding: '10px 12px' }}>
                        <div className="pf-tile-label" style={{ fontSize: 11 }}>{finding.short}</div>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 2 }}>
                          <span style={{ fontSize: 22, fontWeight: 600 }}>{num(total.count)}</span>
                          <PfChip tone={sev.tone}>{sev.word}</PfChip>
                        </div>
                        <div className="pf-tile-sub" style={{ fontSize: 11 }}>
                          {total.count ? `${clock(total.duration_sec)} in total` : 'not detected'}
                        </div>
                      </div>
                    )
                  })}
                  <div className="pf-tile" style={{ padding: '10px 12px' }}>
                    <div className="pf-tile-label" style={{ fontSize: 11 }}>Drowsiness</div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 2 }}>
                      <span style={{ fontSize: 22, fontWeight: 600 }}>
                        {drowsy == null ? '—' : `${num(drowsy, { decimals: 1 })}%`}
                      </span>
                    </div>
                    <div className="pf-tile-sub" style={{ fontSize: 11 }}>of the recording</div>
                  </div>
                </div>

                {FINDINGS.map((finding) => {
                  const rows = report[finding.key] || []
                  if (rows.length === 0) return null
                  const total = totals[finding.key] || { count: 0, duration_sec: 0 }
                  const sev = severityOf(finding.key, total.duration_sec, total.count)
                  return (
                    <section key={finding.key}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap',
                                    borderBottom: '1px solid var(--pf-border)', paddingBottom: 6 }}>
                        <strong style={{ fontSize: 15 }}>{finding.label}</strong>
                        <PfChip tone={sev.tone}>{sev.word}</PfChip>
                        <span className="pf-tile-sub" style={{ fontSize: 12 }}>
                          {num(total.count)} time{total.count === 1 ? '' : 's'} · {clock(total.duration_sec)} in total
                        </span>
                      </div>
                      <div className="pf-tile-sub" style={{ fontSize: 11.5, margin: '6px 0 10px' }}>
                        {finding.hint}
                      </div>

                      <div style={{ display: 'grid', gap: 12,
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))' }}>
                        {rows.map((row, position) => (
                          <Evidence key={`${finding.key}-${row.index}`}
                                    jobId={jobId} kind={finding.key} row={row} position={position}
                                    onOpen={setLightbox} onError={onError} />
                        ))}
                      </div>
                    </section>
                  )
                })}
              </div>
              --- END OLD --- */}

          <div className="pf-report-head">
            <div className="pf-report-avatar" aria-hidden="true">AN</div>
            <div className="pf-report-who">
              <h3 title={report.room_id || jobId}>{report.room_id || jobId}</h3>
              <div className="pf-report-meta">
                {/* The recording's LENGTH, from the frames the engine returned. Not a start time:
                    the engine's origin is synthetic, so a clock here would be the same fiction the
                    timeline just stopped telling. */}
                {recorded != null && <span><ClockIcon />{clock(recorded)} of recording</span>}
                <span>{num(report.frame_count || 0)} frames analysed</span>
              </div>
            </div>
            <img className="pf-report-logo" src={logoUrl} alt="InCruiter" />
          </div>

          <Headline totals={totals} drowsy={drowsy} />

          <div className="pf-report-band">Proctoring Logs</div>

          <div className="pf-logs">
            <div className="pf-report-card">
              <div className="pf-report-card-head"><MonitorIcon />Proctoring Events Log</div>
              {moments.length === 0 ? (
                <PfEmpty title="Nothing was flagged"
                         hint="The analysis found no look-aways, face problems or devices." />
              ) : (
                <>
                  {/* Where the clock on every row is measured FROM, said once. Without this the
                      column of "0:00, 1:51, 2:20" could be read as clock times again. */}
                  <div className="pf-logs-date">
                    Times are measured from the start of the recording
                    {recorded != null ? ` · ${clock(recorded)} long` : ''}
                  </div>
                  <div className="pf-logs-rail">
                    {moments.map(({ finding, row }) => {
                      const sev = severityOf(finding.key, row.duration_sec, 1)
                      const detail = row.item || row.face_status || null
                      return (
                        <div className="pf-logs-row" key={`${finding.key}-${row.index}`}>
                          <div className="pf-logs-time"
                               title={`${clock(offsetOf(row.start, origin) ?? 0)} into the recording`}>
                            {atLabel(row.start, origin)}
                          </div>
                          <span className={`pf-logs-dot ${DOT_BY_TONE[sev.tone] || 'low'}`}
                                title={`${sev.word} — ${clock(row.duration_sec)}`} />
                          <div className="pf-logs-text">
                            {finding.label} for {clock(row.duration_sec)}
                            {detail ? ` · ${detail}` : ''}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </>
              )}
            </div>

            <div className="pf-logs-col">
              <div className="pf-report-card">
                <div className="pf-report-card-head">
                  <CameraIcon />Proctoring Events Screenshots
                </div>
                {withProof.length === 0 ? (
                  <PfEmpty title="No pictures returned"
                           hint="The engine flagged these moments but sent no frames for them." />
                ) : (
                  <div className="pf-logs-shots">
                    {withProof.map(({ finding, row }, position) => (
                      <Evidence key={`${finding.key}-${row.index}`}
                                jobId={jobId} kind={finding.key} row={row} position={position}
                                finding={finding} origin={origin}
                                onOpen={setLightbox} onError={onError} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* [2026-09-25] CHANGED — this disclaimer is OFF on the report. Asked for by Rujal: the
              "can be wrong" wording is not something a client should read on their report.
              `false &&` rather than a deletion, so the sentence is one edit away from coming back.
              OLD: <PfNotice kind="warn"> … </PfNotice> (rendered unconditionally) */}
          {false && (
          <PfNotice kind="warn">
            Findings are produced automatically and can be wrong — a candidate looking down at
            notes and one looking at a phone can appear similar to the model. Treat this as a
            prompt to review the evidence yourself, not as a conclusion about the candidate.
          </PfNotice>
          )}
        </div>
      )}

      {lightbox && (
        <div onClick={() => setLightbox(null)}
             style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(10,16,28,.86)',
                      display: 'grid', placeItems: 'center', padding: 24, cursor: 'zoom-out' }}>
          <figure style={{ margin: 0, maxWidth: '100%', maxHeight: '100%' }}>
            <img src={lightbox.url} alt={lightbox.caption}
                 style={{ maxWidth: '100%', maxHeight: '82vh', borderRadius: 8, display: 'block' }} />
            <figcaption style={{ color: '#fff', fontSize: 13, marginTop: 10, textAlign: 'center' }}>
              {lightbox.caption}
            </figcaption>
          </figure>
        </div>
      )}
    </PfModal>
  )
}


/** The one-sentence, verdict-free summary at the top. */
function Headline({ totals, drowsy }) {
  const flagged = FINDINGS.reduce((sum, f) => sum + (totals[f.key]?.count || 0), 0)
  if (flagged === 0) {
    return (
      <PfNotice kind="good">
        <strong>Nothing was flagged.</strong> The candidate stayed in view and on screen throughout,
        and no phone or second screen was detected.
      </PfNotice>
    )
  }
  const parts = FINDINGS
    .filter((f) => (totals[f.key]?.count || 0) > 0)
    .map((f) => `${totals[f.key].count}× ${f.label.toLowerCase()}`)
  return (
    <PfNotice kind="warn">
      <strong>{num(flagged)} thing{flagged === 1 ? '' : 's'} to review.</strong>{' '}
      {parts.join(', ')}
      {/* [2026-09-16] CHANGED — stated whenever the engine reported one, not only above 10%.
          The four-tile strip that used to carry this number unconditionally is commented out in
          ReportModal, so this sentence is now the only place it appears. A number that quietly
          stops being shown below a threshold is a number nobody can rely on.
          OLD: {drowsy != null && drowsy >= 10 ? `, and drowsiness in …` : ''} */}
      {drowsy != null ? `, and drowsiness in ${num(drowsy, { decimals: 0 })}% of the recording` : ''}.
      Every moment below is shown with the picture the camera captured at the time.
    </PfNotice>
  )
}


/** One flagged moment, as a picture with its time underneath.
 *
 *  [2026-09-06] CHANGED — the image now loads BY ITSELF instead of behind a "Show picture" toggle.
 *
 *  WHY IT WAS BEHIND A CLICK, AND WHY THAT WAS THE WRONG CALL. The engine embeds a full base64
 *  JPEG in every interval, so the whole report is 4.3 MB — and I made the reviewer opt in to each
 *  frame to avoid that. But the pictures ARE the report: a list of timestamps saying "face not
 *  visible for 14 seconds" is not something a recruiter can act on, and asking them to click 91
 *  times to see the evidence means most of it never gets looked at.
 *
 *  WHAT MAKES LOADING THEM ALL AFFORDABLE. Each frame is a SEPARATE request for ~40 KB, served from
 *  its own URL and browser-cached for 5 minutes (app/modules/me/route.py). So this is a normal
 *  image gallery — the same thing any page with 91 thumbnails does — not a 4.3 MB JSON blob that
 *  must arrive complete before anything renders. `loading="lazy"` means the browser fetches only
 *  what is scrolled into view, so a report with 91 findings costs a handful of requests until the
 *  reviewer scrolls, and the connection limit paces the rest.
 *
 *  The object URL is revoked on unmount, or a reviewer closing a report leaks every frame it showed.
 */
// [2026-09-16] CHANGED — `finding` and `origin` added, so a card can name what it is evidence OF
// and stamp itself with a true offset instead of the engine's synthetic clock. The FETCH IS
// UNTOUCHED: same request, same abort, same object-URL revoke. Only the markup below changed, to
// the .pf-shot card the SDK session report already uses — one card, one place to fix it.
// OLD: function Evidence({ jobId, kind, row, position, onOpen, onError }) {
function Evidence({ jobId, kind, row, position, finding, origin, onOpen, onError }) {
  const [url, setUrl] = useState(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (!row.has_proof) return undefined
    const controller = new AbortController()
    let objectUrl = null
    platformProofObjectUrl(jobId, kind, row.index, controller.signal)
      .then((made) => { objectUrl = made; setUrl(made) })
      .catch((err) => {
        if (controller.signal.aborted) return
        // Shown in place, not raised: one unreadable frame must not blank the whole report.
        setFailed(true)
        onError?.(err)
      })
    return () => {
      controller.abort()
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [jobId, kind, row.index, row.has_proof, onError])

  // The engine's own classification, in words. `item` for devices, `face_status` for faces.
  const detail = row.item || row.face_status || null
  // [2026-09-16] CHANGED — the caption is the OFFSET, not the engine's clock. See offsetOf.
  // OLD: const caption = `${timeOf(row.start)} · ${clock(row.duration_sec)}${detail ? ` · ${detail}` : ''}`
  const at = atLabel(row.start, origin)
  const caption = `${at} into the recording · ${clock(row.duration_sec)}${detail ? ` · ${detail}` : ''}`
  const sev = severityOf(kind, row.duration_sec, 1)
  // Grey, not green, for the lowest band — same reasoning as DOT_BY_TONE: every card here is a
  // flagged moment, and green would read as "this one was fine".
  const tone = { bad: '#f2464b', warn: '#e8821e' }[sev.tone] || 'var(--pf-ink-2)'

  // [2026-09-16] CHANGED — the .pf-shot card, so this grid and the SDK session report's are the
  // same component to the eye. The old inline-styled figure is kept below, whole.
  //
  // --- OLD (pre-2026-09-16) ---
  // <figure style={{ margin: 0 }}>
  //   <div style={{ position: 'relative', aspectRatio: '4 / 3', borderRadius: 8,
  //                 overflow: 'hidden', background: 'var(--pf-sunken)',
  //                 border: '1px solid var(--pf-border)' }}>
  //     {!row.has_proof ? (
  //       <div style={{ display: 'grid', placeItems: 'center', height: '100%' }}>
  //         <span className="pf-tile-sub" style={{ fontSize: 11.5 }}>No picture</span>
  //       </div>
  //     ) : failed ? (
  //       <div style={{ display: 'grid', placeItems: 'center', height: '100%' }}>
  //         <span className="pf-tile-sub" style={{ fontSize: 11.5 }}>Could not load</span>
  //       </div>
  //     ) : url ? (
  //       <button onClick={() => onOpen({ url, caption })}
  //               aria-label={`Enlarge the picture from ${timeOf(row.start)}`}
  //               style={{ display: 'block', width: '100%', height: '100%', padding: 0, border: 0,
  //                        background: 'none', cursor: 'zoom-in' }}>
  //         <img src={url} alt={`What the camera saw at ${timeOf(row.start)}`} loading="lazy"
  //              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
  //       </button>
  //     ) : <PfSkeleton height={140} />}
  //     <span style={{ position: 'absolute', top: 6, left: 6, fontSize: 10.5, fontWeight: 600,
  //                    padding: '2px 6px', borderRadius: 20, background: 'rgba(10,16,28,.72)',
  //                    color: '#fff' }}>
  //       {position + 1}
  //     </span>
  //   </div>
  //   <figcaption style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6,
  //                        flexWrap: 'wrap' }}>
  //     <span className="mono" style={{ fontSize: 12.5, fontWeight: 600 }}>{timeOf(row.start)}</span>
  //     <span className="pf-tile-sub" style={{ fontSize: 11.5 }}>for {clock(row.duration_sec)}</span>
  //     {detail && <PfChip tone="idle">{detail}</PfChip>}
  //   </figcaption>
  // --- END OLD ---
  return (
    <figure className="pf-shot">
      <div className="pf-shot-img">
        {!row.has_proof ? (
          <div className="pf-shot-placeholder">No picture</div>
        ) : failed ? (
          <div className="pf-shot-placeholder">Could not load</div>
        ) : url ? (
          // A button, not a bare <img onClick>: enlarging is an action and must be reachable by
          // keyboard. `loading="lazy"` is what keeps a 91-finding report cheap — see the note above.
          <button onClick={() => onOpen({ url, caption })}
                  aria-label={`Enlarge the picture from ${at} into the recording`}
                  style={{ display: 'block', width: '100%', height: '100%', padding: 0, border: 0,
                           background: 'none', cursor: 'zoom-in' }}>
            <img src={url} alt={`What the camera saw ${at} into the recording`} loading="lazy" />
          </button>
        ) : <div className="pf-shot-placeholder">Loading…</div>}
      </div>

      <figcaption>
        <div className="t">Screenshot #{position + 1}</div>
        <div className="d" style={tone ? { color: tone } : undefined}>
          {finding?.label || 'Flagged moment'} for {clock(row.duration_sec)}
          {detail ? ` · ${detail}` : ''}
        </div>
        <div className="pf-shot-foot">
          <span className="when" title={`${at} into the recording`}>
            <ClockIcon />{at} into the recording
          </span>
          {url && (
            <span className="pf-shot-acts">
              <button type="button" onClick={() => onOpen({ url, caption })}
                      title="View full size"
                      aria-label={`View screenshot ${position + 1} full size`}>
                <EyeIcon />
              </button>
            </span>
          )}
        </div>
      </figcaption>
    </figure>
  )
}
